import "../lodgement-tab/lodgement-tab.scss";
import { useEffect, useMemo, useState } from "react";
import { DocumentItem, FileItem } from "../../../../../models/polymer/Document.model";
import { P2Accordion, P2Alert, P2Button, P2ButtonGroup, P2DateSelector, P2TextInput, P2TextLabel } from "react-bootstrap-common-script";
import { Separator } from "../../../../separator";
import DocumentationLists from "../../../../../pages/application-coo/documents/documentation-list";
import config from "../../../../../config";
import { PolymerAxios } from "../../../../../utils/axios";
import { useAppSelector } from "../../../../../redux/hook";
import { API_Solicitor_LodgementSubmit } from "../../../../../utils/api";
import { INSTRUMENT_DROPDOWN_OPTIONS } from "./instrument-type-dropdown-list";
import { InstrumentTypeEnum } from "../../../../../models/enum/instrument-type.enum";
import { SubmitDocuments } from "../../../../../models/classes/CooSubmission.class";
import { useSolicitorCaseDetailsContext } from "../../../../../context/solicitor-case-details-context";
import { InstrumentDisplay, InstrumentDto } from "../../../../../models/classes/solicitor/Instrument.class";
import { SolicitorLodgementRequest } from "../../../../../models/classes/solicitor/SolicitorCompletionDetail.class";
import DropdownMulti from "../../../../dropdown-multi/dropdown-multi";
import useIsMobile from "../../../../../hooks/use-is-mobile";
import { INSTRUMENT_TYPE_MAP } from "../../../../../utils/shared/constants";

const LODGEMENT_INFO_DOCUMENTATIONS = [
  new DocumentItem({
    label: "Successful Notification of Registration of Instruments (SNRI)",
    description: "Please submit the SNRI after you have successfully submit the lodgement to SLA.",
    checkbox: false,
    checkbox_label: "",
    code: "SDC12",
    sample: "",
  }),
];

const PRESELECTED_VALUES: InstrumentTypeEnum[] = ["TRANSFER"];

const LodgementTab = () => {
  const queryParameters = new URLSearchParams(window.location.search);
  const applicationUuid = queryParameters.get("applicationUuid");

  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const { uen } = useAppSelector((state) => state.auth.AuthenticationSlice.corpPassDetails);
  const { isMobile } = useIsMobile();

  // States
  const [instruments, setInstruments] = useState<InstrumentDisplay[]>([]);
  const [selectedValues, setSelectedValues] = useState<string[]>(PRESELECTED_VALUES);
  const [lodgementDate, setLodgementDate] = useState<string>("");
  const [fileItemsStorage, setFileItemsStorage] = useState<FileItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAlert, setSubmitAlert] = useState<{ variant: "warning" | "error"; text: string } | null>(null);

  const { solicitorSubmissionsRefetch, solicitorCompletionRefetch, solicitorCompletionData } = useSolicitorCaseDetailsContext();

  const actualCompletionDate = solicitorCompletionData?.completion?.actual_completion_date ?? "";

  // Derived values
  const isLodgementDateOnOrAfterActualCompletion = useMemo(() => {
    if (!lodgementDate) return false;
    if (!actualCompletionDate) return true;
    return lodgementDate >= actualCompletionDate;
  }, [lodgementDate, actualCompletionDate]);

  const isLodgementDateValid = lodgementDate !== "" && isLodgementDateOnOrAfterActualCompletion;

  const areInstrumentsValid = useMemo(
    () => instruments.length > 0 && instruments.every((instrument) => instrument.uuid && instrument.instrument_type && instrument.number),
    [instruments]
  );

  const isDocumentValid = useMemo(
    () => config.isLocalhost || (fileItemsStorage.length > 0 && fileItemsStorage.every((e) => e.key !== "")),
    [config.isLocalhost, fileItemsStorage]
  );

  const isSubmitButtonEnabled =
    isLodgementDateValid && areInstrumentsValid && isDocumentValid && !isSubmitting;

  // Functions
  function createHandleChangeInstrumentNumber(uuid: string) {
    return (instrumentNumber: string) => {
      const targetIndex: number = instruments.findIndex((instrument) => instrument.uuid === uuid);

      if (targetIndex === -1) return () => {};

      setInstruments((prev) => {
        prev[targetIndex] = {
          ...prev[targetIndex],
          number: instrumentNumber,
        };
        return [...prev];
      });
    };
  }

  function createHandleChangeRegistrationDate(uuid: string) {
    return (registrationDate: string) => {
      const targetIndex: number = instruments.findIndex((instrument) => instrument.uuid === uuid);

      if (targetIndex === -1) return () => {};

      setInstruments((prev) => {
        prev[targetIndex] = {
          ...prev[targetIndex],
          registration_date: registrationDate,
        };
        return [...prev];
      });
    };
  }

  async function handleSubmit() {
    let documents = fileItemsStorage.map((file) => {
      //NOTE: required_option_checked not track from application-step-4;
      let fileType = "";
      if (file?.file_content?.file_name) {
        const fileNameSplitArray = file?.file_content?.file_name.split(".");
        fileType = fileNameSplitArray[fileNameSplitArray.length - 1];
      }
      return new SubmitDocuments({
        uuid: file.file_id.toString(),
        code: file.code,
        file_name: file.file_content?.file_name,
        label: file.label,
        document_type: fileType,
        required_option_checked: true,
        key: file.key,
        file_size: file.file_content.size,
      });
    });

    let payload = new SolicitorLodgementRequest({
      application_uuid: applicationUuid ?? "",
      documents,
      instruments: instruments.map((instrumentDisplay) => InstrumentDto.mapInstrumentDisplayToDto(instrumentDisplay)),
      lodgement_date: lodgementDate,
      submitted_by: loginUser.name,
    });

    setIsSubmitting(true);
    setSubmitAlert(null);

    try {
      const response = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken, uen).post(`${API_Solicitor_LodgementSubmit}`, payload);
      if (response.status === 200) {
        solicitorSubmissionsRefetch();
        solicitorCompletionRefetch();
      }
    } catch (e: unknown) {
      await solicitorCompletionRefetch();

      const status =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { status?: number } }).response?.status
          : undefined;

      if (status === 422) {
        setSubmitAlert({
          variant: "warning",
          text: "This lodgement has already been submitted.",
        });
      } else {
        setSubmitAlert({
          variant: "error",
          text: "An error occurred while submitting the lodgement information. Please try again later.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // Effects
  useEffect(() => {
    setInstruments((prev) => {
      // Check for removed instrument
      const filteredInstrument = prev.filter((instrument) => {
        const predicate = selectedValues.includes(instrument.instrument_type);
        return predicate;
      });

      // Check for added items
      selectedValues.forEach((value) => {
        if (!filteredInstrument.some((instrument) => instrument.instrument_type === value)) {
          const title = INSTRUMENT_TYPE_MAP.get(value as InstrumentTypeEnum) ?? "";
          filteredInstrument.push(new InstrumentDisplay(title, value as InstrumentTypeEnum, "", ""));
        }
      });

      return filteredInstrument;
    });
  }, [selectedValues]);

  return (
    <div style={{ paddingLeft: 24, paddingRight: 24 }}>
      <h2 className="polymer-completion-tab__title">Lodgement Information</h2>
      <p style={{ marginBottom: 16 }}>Please update the lodgement information below after you have completed the lodgement to SLA.</p>

      <div className="polymer-lodgement-tab__info-alert">
        <P2Alert
          variant="info"
          withBorder={true}
          text="Please key in only the final transfer instrument lodgement details under Instrument Type = Transfer. If there are other lodgement information in between (example double transfers), please select Instrument Type = Others."
          style={{ marginBottom: 16 }}
        />
      </div>

      <DropdownMulti
        items={INSTRUMENT_DROPDOWN_OPTIONS}
        itemValues={selectedValues}
        setItemValues={setSelectedValues}
        label="Document Type"
        placeholder="Please select type of document"
        required={true}
        style={{ marginBottom: 8 }}
        identifierKey="value"
        disabledValues={PRESELECTED_VALUES}
      />

      {instruments.map((instrument, index) => {
        const isLastIndex = index === instruments.length - 1;
        return (
          <div style={{ marginBottom: isLastIndex && !isMobile ? 32 : 16 }}>
            <P2Accordion defaultOpen={true} title={<p className="polymer-lodgement-tab__accordion-title">{instrument.title}</p>}>
              <div style={{ marginBottom: 16 }}>
                <P2TextInput
                  required
                  value={instrument.number}
                  onChange={createHandleChangeInstrumentNumber(instrument.uuid)}
                  name="instrument_number"
                  label="Instrument number"
                  maxLength={10}
                  placeholder="xxxx-xxxxxx-x"
                  type="text"
                />
              </div>
              <P2DateSelector
                value={instrument.registration_date}
                required
                placeholder="dd/mm/yyyy"
                label="Date registered with SLA"
                onChange={createHandleChangeRegistrationDate(instrument.uuid)}
              />
            </P2Accordion>
          </div>
        );
      })}

      <Separator variant="dashed" style={{ marginBottom: !isMobile ? 32 : 16 }} />
      <div style={{ marginBottom: !isMobile ? 32 : 16 }}>
        <P2DateSelector
          isError={!!lodgementDate && !isLodgementDateOnOrAfterActualCompletion}
          errorMsg="Lodgement date must be on or after the actual completion date"
          value={lodgementDate}
          required
          placeholder="DD/MM/YYYY"
          label="Lodgement date"
          minInput={actualCompletionDate || undefined}
          onChange={setLodgementDate}
        />
      </div>
      <Separator variant="dashed" style={{ marginBottom: !isMobile ? 32 : 16 }} />
      <div style={{ marginBottom: 32 }}>
        <DocumentationLists
          type={1}
          document_list={LODGEMENT_INFO_DOCUMENTATIONS}
          filesItemsStorage={fileItemsStorage}
          setFileItemsStorage={setFileItemsStorage}
        />
      </div>
      <Separator variant="dashed" style={{ marginBottom: 32 }} />
      {submitAlert ? (
        <P2Alert variant={submitAlert.variant} text={submitAlert.text} style={{ marginBottom: 16 }} />
      ) : null}
      <P2ButtonGroup>
        <P2Button onClick={handleSubmit} disabled={!isSubmitButtonEnabled} variant="primary-1">
          {isSubmitting ? "Submitting..." : "Submit"}
        </P2Button>
      </P2ButtonGroup>
    </div>
  );
};

export default LodgementTab;
